import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  Search, 
  Check, 
  Tag,
  Loader2
} from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useCategories } from '../context/CategoryContext';

interface CategoryFilterProps {
  value: string;
  onChange: (category: string) => void;
  placeholder?: string;
  className?: string;
  zIndex?: number;
}

export function CategoryFilter({ value, onChange, placeholder = "Select category", className = "", zIndex = 1000 }: CategoryFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use global category context
  const { categories, loading } = useCategories();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectCategory = (categoryName: string) => {
    onChange(categoryName);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Create filter options including "All", "Uncategorized", and existing categories
  const allCategories = [
    { id: 'all', name: 'All' }, 
    { id: 'uncategorized', name: 'Uncategorized' },
    ...categories
  ];
  
  const filteredCategories = allCategories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDisplayValue = () => {
    if (value === 'All') return 'All Categories';
    if (value === 'Uncategorized') return 'Uncategorized';
    return value || placeholder;
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`} style={{ zIndex }}>
      {/* Selected Value Display / Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-jamaica-green/20 focus:border-jamaica-green transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Tag className="w-4 h-4 text-gray-400 shrink-0" />
          {value && value !== 'All' ? (
            <Badge variant="outline" className={`text-xs ${value === 'Uncategorized' ? 'bg-orange-50 text-orange-700 border-orange-200' : ''}`}>
              {value}
            </Badge>
          ) : (
            <span className={`truncate ${value === 'All' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {getDisplayValue()}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-hidden"
            style={{
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search categories..."
                  className="pl-9 text-sm border-0 focus:ring-0 focus:border-0"
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                  <span className="ml-2 text-sm text-gray-600">Loading categories...</span>
                </div>
              ) : (
                <>
                  {/* Categories Options */}
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleSelectCategory(category.name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center justify-between group"
                      >
                        <span className={`truncate ${
                          category.name === 'All' ? 'font-medium text-gray-900' : 
                          category.name === 'Uncategorized' ? 'font-medium text-orange-700' : ''
                        }`}>
                          {category.name === 'All' ? 'All Categories' : category.name}
                        </span>
                        {value === category.name && (
                          <Check className="w-4 h-4 text-jamaica-green" />
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      {searchTerm ? 'No categories found' : 'No categories available'}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 